/**
 * Store Implementation Module
 * 
 * This module provides the implementation of reactive stores.
 * It includes both the legacy store implementation (oldAppStore) and
 * the new reactive store implementation.
 * 
 * The reactive store implementation uses the Proxy API to intercept
 * property changes and notify subscribers when properties change.
 * 
 * This module also registers the default store with the store registry
 * so that components can access it without creating circular dependencies.
 */

// import { atom, Atom,deepMap, WritableAtom } from "nanostores";
// import { ParamateConfigurator } from "../ext/paramate-frontend/index.ts";

import type { IReactiveComponent, IReactiveStore } from "../as-components";
import type { Project } from "../data/projectManagement.ts";
import type { StoredNotification } from '../data/data-interfaces.external.ts';
import type { VPData } from '../data/data-interfaces.internal.ts';
import { registerStore } from "./store-registry.ts";

import { DEFAULT_ASSETS_PATH, DEFAULT_ICON_LIST, DEFAULT_SERVER_ROOT, DEV_CDN_ROOT, fullAppSuiteRootURL, inIframe, isProduction, localDev, useLocalAssets } from "../data/constants.ts";

/**
 * Notifies all subscribers of a property change
 * 
 * This function is called by the Proxy handler when a property changes.
 * It notifies all subscribers of the changed property.
 * 
 * @param store The store object
 * @param prop The property that changed
 */
function notifySubscribers(store: IReactiveStore, prop: string) {
  const components = store.subscribers.get(prop);

  if (components) {
    components.forEach(component => {
      component.updateSubbedValues({ [prop]: store[prop] });
    });
  }
}

function triggerSideEffects(store: IReactiveStore, prop: string) {
  const effects = store.sideEffects.get(prop);
  if (effects) {
    effects.forEach(effect => effect(store[prop]));
  }
}

/**
 * Creates a reactive store with the provided schema
 * 
 * This function creates a reactive store that notifies subscribers
 * when properties change. It uses the Proxy API to intercept property
 * changes and notify subscribers.
 * 
 * @param schema Initial store schema with default values
 * @returns A proxy object that notifies subscribers when properties change
 */
export function reactiveStore<T extends Record<string, any>>(schema: T): IReactiveStore {
  // Create a store object with the schema and subscribers map
  const storeObj = {
    ...schema,
    emitUpdateEvent: true,
    sideEffects: new Map<string, Set<(value: any) => void>>(),

    subscribers: new Map<string, Set<IReactiveComponent>>(),

    /**
     * Subscribe a component to specific properties
     * 
     * This method adds a component to the list of subscribers for the
     * specified properties. When those properties change, the component
     * will be notified.
     * 
     * @param component The component to subscribe
     * @param props Array of property names to subscribe to
     * @returns Object with current values of subscribed properties
     */
    subscribe(component: IReactiveComponent, props: string[]): { [key: string]: any } {
      // devlog(this, "subscribing to", props);
      props.forEach(prop => {
        if (!this.subscribers.has(prop)) {
          this.subscribers.set(prop, new Set<IReactiveComponent>());
        }
        this.subscribers.get(prop)?.add(component);
      });
      return props.reduce((acc, prop) => {
        acc[prop] = this[prop as keyof typeof this];
        return acc;
      }, {});
    },

    /**
     * Manually re-run reactivity for a property without reassigning it.
     *
     * The store proxy's `set` trap only fires on top-level reassignment, so
     * mutating a nested field (e.g. `currentProject.metadata.name`) does NOT
     * notify subscribers or side effects. Call this after such an in-place
     * mutation to fan the change out to subscribers/side effects, exactly as a
     * reassignment would.
     *
     * @param prop The property whose subscribers/side effects should be re-run
     */
    notify(prop: string): void {
      notifySubscribers(this as unknown as IReactiveStore, prop);
      triggerSideEffects(this as unknown as IReactiveStore, prop);
      if (this.emitUpdateEvent) {
        window.dispatchEvent(new CustomEvent('store-update', {
          detail: { store: this, prop, value: this[prop as keyof typeof this] }
        }));
      }
    },

    /**
     * Add a side effect that triggers when a property changes
     *
     * @param prop The property to watch for changes
     * @param effect The function to call when the property changes
     * @returns A function that when called will remove this side effect
     */
    addSideEffect(prop: string, effect: (value: any) => void): () => void {
      if (!this.sideEffects.has(prop)) {
        this.sideEffects.set(prop, new Set<(value: any) => void>());
      }
      this.sideEffects.get(prop)?.add(effect);

      // Return a function to remove this specific effect
      return () => this.removeSideEffect(prop, effect);
    },

    /**
     * Remove a specific side effect for a property
     * 
     * @param prop The property the effect is registered for
     * @param effect The effect function to remove
     * @returns Boolean indicating if the effect was successfully removed
     */
    removeSideEffect(prop: string, effect: (value: any) => void): boolean {
      const effects = this.sideEffects.get(prop);
      if (!effects) return false;

      const result = effects.delete(effect);

      if (effects.size === 0) {
        this.sideEffects.delete(prop);
      }

      return result;
    },

    /**
     * Remove all side effects for a specific property
     * 
     * @param prop The property to remove all side effects for
     * @returns Boolean indicating if any effects were removed
     */
    clearSideEffects(prop: string): boolean {
      return this.sideEffects.delete(prop);
    },

    /**
     * Unsubscribe a component from all properties
     * 
     * This method removes a component from the list of subscribers
     * for all properties.
     * 
     * @param component The component to unsubscribe
     */
    unsubscribe(component: IReactiveComponent) {
      this.subscribers.forEach((components, prop) => {
        components.delete(component);
        if (components.size === 0) {
          this.subscribers.delete(prop);
        }
      });
    },
  };

  // Create a proxy to intercept property changes
  return new Proxy(storeObj, {
    set(target, prop, value) {
      target[prop as keyof typeof target] = value;
      notifySubscribers(target, prop as string);
      triggerSideEffects(target, prop as string);
      if (target.emitUpdateEvent) {
        window.dispatchEvent(new CustomEvent('store-update', {
          detail: {
            store: target,
            prop: prop as string,
            value: value
          }
        }));
      }
      return true;
    }
  });
}
export interface IconListing {
  [iconName: string]: { [iconVariant: string]: string }
}
/**
 * Default store instance
 * 
 * This is the default store instance that is used by components
 * that don't specify a store name.
 */
export const liveAppData = reactiveStore({
  userData: null,
  isAuthenticated: false,
  pendingAuth: true,
  testText: "test",
  serverRoot: DEFAULT_SERVER_ROOT,
  assetsPath: DEFAULT_ASSETS_PATH,
  useLocalAssets: useLocalAssets,
  appId: undefined,
  appLangFileName: undefined,
  isProduction: isProduction,
  
  unsavedChanges: false as boolean,
  get imgDir() {
    // srcdoc iframes (as-code-demo): window.location is 'about:srcdoc'.
    // A <base href> tag points at the parent page's directory, so resolving
    // './' against document.baseURI gives the correct asset root.
    // Regular same-origin iframes (e.g. grabcadTester) must NOT use this path —
    // their document.baseURI is a full URL with filename/query that produces
    // a broken path when used as a directory.
    if (window.location.protocol === 'about:') {
      return `${new URL('./', document.baseURI).href}${this.assetsPath}/img`;
    }
    if (this.useLocalAssets) return `/${this.assetsPath}/img`;
    if (localDev) return `${DEV_CDN_ROOT}/${this.assetsPath}/img`;
    return `${fullAppSuiteRootURL}/${this.assetsPath}/img`
  },

  _iconList: DEFAULT_ICON_LIST,

  // UI ============================
  uiInputs: {} as Record<string, any>,
  
  notificationHistory: [] as StoredNotification[],


  appEnv: null as string | null,

  // Whether the current app may save files locally. Set from the appList's
  // per-app downloadEnabled flag (license-gated) once as-app knows its appId.
  canDownload: true as boolean,

  // Project management =================================
  defaultProjectHandlingEnabled: true as boolean,
  currentProject: null as Project | null,


  savedProjects: [] as Project[],


  // Notification history (capped at MAX_STORED_NOTIFICATIONS)

  // Set to true when IndexedDB is unavailable (e.g. iOS Lockdown Mode)
  noDb: false as boolean,
});

/**
 * Registry entry for a paramate-linked input
 */
export interface ParamateLinkedInput {
  element: WeakRef<HTMLElement>;
  paramName: string;
  pendingValue: any | null;
  isPending: boolean;
}

/**
 * Registry for paramate-linked inputs
 * Maps param names (without 'main:' prefix) to their linked inputs
 */
export const paramateInputRegistry = new Map<string, Set<ParamateLinkedInput>>();

/**
 * Register an input element as linked to a paramate param
 */
export function registerParamateInput(paramName: string, element: HTMLElement): ParamateLinkedInput {
  if (!paramateInputRegistry.has(paramName)) {
    paramateInputRegistry.set(paramName, new Set());
  }

  const entry: ParamateLinkedInput = {
    element: new WeakRef(element),
    paramName,
    pendingValue: null,
    isPending: false
  };

  paramateInputRegistry.get(paramName)!.add(entry);
  return entry;
}

/**
 * Unregister an input element from the paramate registry
 */
export function unregisterParamateInput(paramName: string, element: HTMLElement): void {
  const entries = paramateInputRegistry.get(paramName);
  if (!entries) return;

  for (const entry of entries) {
    if (entry.element.deref() === element) {
      entries.delete(entry);
      break;
    }
  }

  if (entries.size === 0) {
    paramateInputRegistry.delete(paramName);
  }
}

/**
 * Mark all inputs for a param as pending
 */
export function markParamPending(paramName: string, pendingValue: any): void {
  const entries = paramateInputRegistry.get(paramName);
  if (!entries) return;

  for (const entry of entries) {
    const el = entry.element.deref();
    if (el) {
      entry.isPending = true;
      entry.pendingValue = pendingValue;
      el.classList.add('as-pending');
    }
  }
}

/**
 * Clear pending state for all inputs of a param without updating value
 */
export function clearParamPending(paramName: string): void {
  const entries = paramateInputRegistry.get(paramName);
  if (!entries) return;

  for (const entry of entries) {
    const el = entry.element.deref();
    if (el) {
      entry.isPending = false;
      entry.pendingValue = null;
      el.classList.remove('as-pending');
    }
  }
}

/**
 * Clear pending state for all inputs of a param and update with ground truth
 */
export function resolveParamPending(paramName: string, groundTruthValue: any): void {
  const entries = paramateInputRegistry.get(paramName);
  if (!entries) return;

  for (const entry of entries) {
    const el = entry.element.deref();
    if (el) {
      entry.isPending = false;
      entry.pendingValue = null;
      el.classList.remove('as-pending');

      // Dispatch update event to the element
      el.dispatchEvent(new CustomEvent('as-paramate-value-resolved', {
        detail: {
          paramName,
          value: groundTruthValue
        }
      }));
    }
  }
}

/**
 * Check if any registered input has a pending configure value
 */
export function hasPendingConfigureValues(): boolean {
  for (const entries of paramateInputRegistry.values()) {
    for (const entry of entries) {
      if (entry.isPending) return true;
    }
  }
  return false;
}

/**
 * Trigger configure() on the configurator if there are pending values.
 * Values are already staged via setParamValue(..., false); this just
 * kicks off the configure cycle. Pending state is cleared when
 * paramate-configurator-after-configure resolves ground truth.
 */
export function configurePending(configurator: any): void {
  if (!hasPendingConfigureValues()) return;
  configurator.configure();
}

/**
 * Get all registered param names
 */
export function getRegisteredParamNames(): string[] {
  return Array.from(paramateInputRegistry.keys());
}

/**
 * Apply min/max constraints from param data to registered inputs.
 * Writes to the 'auto-min'/'auto-max' attributes (param-owned) on elements whose class
 * observes them. Author-set 'min'/'max' take precedence in the element, so these are
 * written unconditionally and refresh on every configure.
 */
export function applyParamConstraints(paramName: string, paramData: { min?: number; max?: number }): void {
  const entries = paramateInputRegistry.get(paramName);
  if (!entries) return;

  for (const entry of entries) {
    const el = entry.element.deref();
    if (!el) continue;

    const observed = (el.constructor as any).observedAttributes as string[] | undefined;
    if (!observed) continue;

    // Param-driven bounds are written to the `auto-*` attributes (no guard) so they keep
    // updating on every configure. The element combines them with any author-set
    // `min`/`max` by taking the most restrictive of the two.
    if (paramData.min !== undefined && observed.includes('auto-min')) {
      el.setAttribute('auto-min', String(paramData.min));
    }
    if (paramData.max !== undefined && observed.includes('auto-max')) {
      el.setAttribute('auto-max', String(paramData.max));
    }
  }
}

/**
 * Restore UI param values from a saved snapshot (keyed by element ID).
 * Finds each element by ID and sets its value directly.
 */
export function restoreUiInputs(params: Record<string, any>): void {
  liveAppData.uiInputs = { ...params };
  for (const [id, value] of Object.entries(params)) {
    const el = document.getElementById(id);
    if (!el) continue;
    if (el.tagName === 'AS-CHECKBOX') {
      (el as any).checked = value;
    } else if ('setValue' in el) {
      (el as any).setValue(value);
    } else {
      (el as any).value = value;
    }
  }
}

/**
 * Clear UI inputs (e.g. on new project).
 */
export function clearUiInputs(): void {
  liveAppData.uiInputs = {};
}



// Register the store with the registry
registerStore('default', liveAppData);

// ============================================================================
// Debug globals
// ============================================================================

declare global {
  interface Window {
    asGlobals: {
      liveAppData: typeof liveAppData;
      currentProjectData: any;
      paramateInputRegistry: typeof paramateInputRegistry;
      triggerExpiry?: () => void;
      invalidateSession?: () => void;
      simulateSleep?: (minutes?: number) => void;
      setCanDownload?: (value?: boolean) => void;
    };
  }
}

window.asGlobals = { liveAppData, currentProjectData: null, paramateInputRegistry };
// Debug hook: override the license-gated download flag (default true).
window.asGlobals.setCanDownload = (value = true) => { liveAppData.canDownload = value; };

/**
/**
 * Proxy that automatically prefixes icon paths with HEADER_IMG_DIR
 */

export const IconList = new Proxy(liveAppData._iconList, {
  get(target, prop) {
    const value = target[prop as keyof typeof target];
    if (typeof value === 'object') {
      return new Proxy(value, {
        get(target, prop) {
          const path = target[prop as keyof typeof target];
          return `${liveAppData.imgDir}/${path}`;
        }
      });
    }
    return value;
  }
});
